import { Inject, Injectable } from '@angular/core';

import makeDebug from '../../makeDebug';
import { IPlatformSync, PlatformSyncToken } from '../common/contracts/sync/platform-sync';
import { toError } from '../common/logging/error-normalization';
import { ClientDataSyncService } from '../shared/services/client-data-sync.service';
import { IClientDataSyncService } from '../shared/services/contracts/sync/client-data-sync';
import { NetworkInfoService } from '../shared/services/network/network-info.service';
import { CommandResult } from './command-result';
import { ICommand } from './contracts/command';

const debug = makeDebug('command:command-sender');

@Injectable({ providedIn: 'root' })
export class CommandSender {
  constructor(
    @Inject(ClientDataSyncService) private _clientDataSyncService: IClientDataSyncService,
    private readonly _networkInfoService: NetworkInfoService,
    @Inject(PlatformSyncToken) private readonly _platformSync: IPlatformSync
  ) {}

  public async send(command: ICommand): Promise<CommandResult> {
    if (this._networkInfoService.isNotConnected) {
      return new CommandResult({ successful: false, error: new Error('Device is not connected') });
    }

    try {
      delete (command.entity as any).changeTracker;

      debug('syncing command with server', command);

      return await this._clientDataSyncService.write(command.channel, command.entity, {
        changes: command.changes,
        additionalSyncInfo: command.additionalSyncInfo,
      });
    } catch (error) {
      const errorCode = (error as any)?.code;
      const sendError = toError(error, `COMMANDSENDER / SEND failed (channel: ${command?.channel})`);
      // Web only: on native a failed send is retried by the queue's drain loop. Tags/extras/
      // fingerprint make the failure filterable in Sentry per channel and error code; Logger.error
      // would drop the message and leave only the bare exception.
      if (!this._platformSync.isCordova) {
        window.logger?.captureErrorWithExtras(
          `COMMANDSENDER / SEND failed (channel: ${command?.channel})`,
          sendError,
          new Map([
            ['commandChannel', command?.channel ?? 'unknown'],
            ['commandErrorCode', String(errorCode ?? 'none')],
          ]),
          'error',
          {
            commandId: command?._id,
            entityId: (command?.entity as any)?._id,
            commandTimestamp: command?.timestamp,
          },
          ['command-sender', command?.channel ?? 'unknown', String(errorCode ?? 'none')]
        );
      }
      return new CommandResult({ successful: errorCode === 409, error: sendError });
    }
  }
}
